import type { PlayerSearchResponse, PlayerIntelligenceDashboard } from '../types/player';
import { authApi } from './apiClient';

export interface Country {
  country: string;
  player_count: number;
}

export interface CountryStatistics {
  total_players: number;
  avg_age: number;
  players_with_contract: number;
  expiring_contracts: number;
  top_positions: { position: string; count: number }[];
  top_clubs: { club: string; count: number }[];
}

export interface CountryPlayersResponse {
  players: any[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export const sportingService = {
  async searchPlayersAI(query: string, limit = 50, offset = 0): Promise<PlayerSearchResponse> {
    try {
      const response = await authApi.post('/sporting/players/search-ai', {
        query,
        limit,
        offset
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Error en la búsqueda de jugadores');
    }
  },

  async getPlayerById(playerId: string): Promise<any> {
    try {
      const response = await authApi.get(`/sporting/players/${playerId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Error al obtener los detalles del jugador');
    }
  },

  async getPlayerPerformances(playerId: string): Promise<any> {
    try {
      const response = await authApi.get(`/sporting/players/${playerId}/performances`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Error al obtener las performances del jugador');
    }
  },

  async getCountries(): Promise<{ countries: Country[]; total: number }> {
    try {
      const response = await authApi.get('/sporting/countries');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Error al obtener lista de países');
    }
  },

  async getPlayersByCountry(
    country: string,
    page = 1,
    perPage = 50,
    position = '',
    sortBy = 'player_name',
    sortOrder = 'ASC',
    includeRetired = false
  ): Promise<CountryPlayersResponse> {
    try {
      const response = await authApi.get(`/sporting/countries/${encodeURIComponent(country)}/players`, {
        params: {
          page,
          per_page: perPage,
          position,
          sort_by: sortBy,
          sort_order: sortOrder,
          include_retired: includeRetired
        }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Error al obtener jugadores del país');
    }
  },

  async getCountryStatistics(country: string): Promise<CountryStatistics> {
    try {
      const response = await authApi.get(`/sporting/countries/${encodeURIComponent(country)}/statistics`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Error al obtener estadísticas del país');
    }
  },

  async getPlayerIntelligenceDashboard(playerId: string): Promise<PlayerIntelligenceDashboard> {
    try {
      const response = await authApi.get(`/sporting/players/${playerId}/intelligence-dashboard`);
      return response.data.dashboard;
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Error al obtener el dashboard de inteligencia');
    }
  },
};